import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import useDataContext from 'data/context/data';

import PRODUCT_SPECIFICATION from '../specifications/products';
import PRODUCT_CATEGORIES from '../specifications/categories';

export const HomePageLink = props => (
  <Link href="/" passHref legacyBehavior>
    {props.children}
  </Link>
);

HomePageLink.propTypes = {
  children: PropTypes.node,
};

export const ShopPageLink = props => (
  <Link href="/shop" passHref legacyBehavior>
    {props.children}
  </Link>
);

ShopPageLink.propTypes = {
  children: PropTypes.node,
};

export const SignInPageLink = props => (
  <Link href="/signin" passHref legacyBehavior>
    {props.children}
  </Link>
);

SignInPageLink.propTypes = {
  children: PropTypes.node,
};

export const ReferFriendPageLink = props => (
  <Link href="/refer-friend" passHref legacyBehavior>
    {props.children}
  </Link>
);

ReferFriendPageLink.propTypes = {
  children: PropTypes.node,
};

export const CheckoutPageLink = props => (
  <Link href="/cart/checkout" passHref legacyBehavior>
    {props.children}
  </Link>
);

CheckoutPageLink.propTypes = {
  children: PropTypes.node,
};

export const CartPageLink = props => (
  <Link href="/cart" passHref legacyBehavior>
    {props.children}
  </Link>
);

CartPageLink.propTypes = {
  children: PropTypes.node,
};

export const TermsPageLink = props => (
  <Link href="/[...params]" as="/terms" passHref legacyBehavior>
    {props.children}
  </Link>
);

TermsPageLink.propTypes = {
  children: PropTypes.node,
};

export const PrivacyPageLink = props => (
  <Link href="/[...params]" as="/privacy" passHref legacyBehavior>
    {props.children}
  </Link>
);

PrivacyPageLink.propTypes = {
  children: PropTypes.node,
};

export const CategoryPageLink = props => {
  const category = props.category ?? PRODUCT_CATEGORIES.find(({ id }) => id === props.categoryId);
  if (!category?.url) {
    return props.children;
  }

  // Let Next.js select dedicated landing pages before the catch-all category route.
  return (
    <Link href={category.url} passHref legacyBehavior>
      {props.children}
    </Link>
  );
};

CategoryPageLink.propTypes = {
  category: PropTypes.object,
  categoryId: PropTypes.string,
  children: PropTypes.node,
};

export const getEditorPageLink = (productId, version, itemId, quantity = 1) => {
  const query = [];
  if (itemId) {
    query.push(`id=${itemId}`);
  }
  if (version) {
    query.push(`version=${version}`);
  }
  if (quantity > 1) {
    query.push(`quantity=${quantity}`);
  }

  let queryString = '';
  if (query.length > 0) {
    queryString = `?${query.join('&')}`;
  }

  const href = `/editor/[...params]${queryString}`;

  let as = `/editor/${productId}`;
  if (itemId) {
    as = `${as}/preview`;
  }
  as = `${as}${queryString}`;

  return { href, as };
};

export const EditorPageLink = props => {
  if (props.productId) {
    const { href, as } = getEditorPageLink(
      props.productId,
      props.version,
      props.itemId,
      props.quantity,
    );

    return (
      <Link {...{ href, as }} passHref legacyBehavior>
        {props.children}
      </Link>
    );
  }

  return null;
};

EditorPageLink.propTypes = {
  productId: PropTypes.string,
  version: PropTypes.string,
  itemId: PropTypes.string,
  quantity: PropTypes.number,
  children: PropTypes.node,
};

export const getProductDetailUrl = (productId, source) => {
  let pageUrl;
  Object.entries(PRODUCT_SPECIFICATION).find(([id, { url, config, skus = [], bundles = [] }]) => {
    if ([id, ...skus, ...bundles].indexOf(productId) >= 0) {
      // Without a source, match by id/sku alone so cards link to the
      // specification url instead of falling back to /{productId}.
      // Entries without url (photo cards) must not stop the search.
      if (url && (source == null || config?.source === source)) {
        pageUrl = url;
        return true;
      }
    }
    return false;
  });

  return pageUrl;
};

export const ProductDetailLink = props => {
  const dataContext = useDataContext();

  let pageUrl = getProductDetailUrl(props.productId, props.source);

  // Product not found, try to find first bundle where this sku is used
  if (!pageUrl) {
    const product = dataContext.getProductBySku(props.productId);
    if (product && PRODUCT_SPECIFICATION[product.id]) {
      pageUrl = PRODUCT_SPECIFICATION[product.id].url;
    }
  }

  // Otherwise route to /{productId}
  if (!pageUrl) {
    pageUrl = `/${props.productId}`;
  }

  return (
    <Link href="/[...params]" as={pageUrl} passHref legacyBehavior>
      {props.children}
    </Link>
  );
};

ProductDetailLink.propTypes = {
  productId: PropTypes.string,
  source: PropTypes.string,
  children: PropTypes.node,
};

export const CardPageLink = props => {
  if (props.productId) {
    return (
      <Link href="/[...params]" as={`/${props.productId}`} passHref legacyBehavior>
        {props.children}
      </Link>
    );
  }
  return (
    <Link
      href="/custom-photo-holiday-cards"
      as="/custom-photo-holiday-cards"
      passHref
      legacyBehavior
    >
      {props.children}
    </Link>
  );
};

CardPageLink.propTypes = {
  productId: PropTypes.string,
  children: PropTypes.node,
};

export const UserPageLink = props => (
  <Link
    href={`/user${props.section !== 'settings' ? `/${props.section}` : ''}`}
    passHref
    legacyBehavior
  >
    {props.children}
  </Link>
);

UserPageLink.propTypes = {
  section: PropTypes.string,
  children: PropTypes.node,
};

UserPageLink.defaultProps = {
  section: 'settings',
};

export const ShippingPageLink = props => (
  <Link href="/shipping" passHref legacyBehavior>
    {props.children}
  </Link>
);

ShippingPageLink.propTypes = {
  children: PropTypes.node,
};

export const AboutPageLink = props => (
  <Link href="/about" passHref legacyBehavior>
    {props.children}
  </Link>
);

AboutPageLink.propTypes = {
  children: PropTypes.node,
};

export const WholesalePageLink = props => (
  <Link href="/wholesale" passHref legacyBehavior>
    {props.children}
  </Link>
);

WholesalePageLink.propTypes = {
  children: PropTypes.node,
};
